import React, { useState, useEffect, useRef, useCallback } from 'react';

// ==========================================
// ✅ EMBEDDED VIDEO/AUDIO CALL COMPONENT
// ==========================================
// Container-based: past binnen het zichtbare scherm
// - Eigen Jitsi server: video.superkonnected.com
// - Gewone iframe (geen @jitsi/react-sdk nodig)
// - Config via URL hash parameters
// - Geen logo's, geen branding, geen prejoin pagina
// - Container met marges zodat Windows taakbalk zichtbaar blijft
// ==========================================

interface JitsiCallProps {
  currentUserId: string;
  currentUserName: string;
  // 1-on-1 call (legacy support)
  otherUserId?: string;
  otherUserName?: string;
  // Group call support
  participants?: Array<{ uid: string; name: string }>;
  roomId?: string;
  callType: 'audio' | 'video';
  onEndCall: () => void;
}

const JitsiCall: React.FC<JitsiCallProps> = ({
  currentUserId,
  currentUserName,
  otherUserId,
  otherUserName,
  participants,
  roomId,
  callType,
  onEndCall
}) => {
  const [isEnding, setIsEnding] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const hangupBtnRef = useRef<HTMLButtonElement | null>(null);
  const fadeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ==========================================
  // ✅ Body scroll blokkeren tijdens call
  // ==========================================
  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  // ==========================================
  // ✅ Ophangen knop: start 100%, na 60s dimmen naar 10%
  // Bij hover: 100%, na 15s weer naar 10%
  // ==========================================
  const fadeToTransparent = useCallback(() => {
    if (hangupBtnRef.current) {
      hangupBtnRef.current.style.transition = 'opacity 2s ease';
      hangupBtnRef.current.style.opacity = '0.10';
    }
  }, []);

  const showButton = useCallback(() => {
    if (hangupBtnRef.current) {
      hangupBtnRef.current.style.transition = 'opacity 0.3s ease';
      hangupBtnRef.current.style.opacity = '1';
    }
    // Reset bestaande timer
    if (fadeTimerRef.current) {
      clearTimeout(fadeTimerRef.current);
    }
  }, []);

  const scheduleHoverFade = useCallback(() => {
    // Na hover: 15 seconden wachten, dan dimmen
    if (fadeTimerRef.current) {
      clearTimeout(fadeTimerRef.current);
    }
    fadeTimerRef.current = setTimeout(() => {
      fadeToTransparent();
    }, 15000);
  }, [fadeToTransparent]);

  // Initieel: na 60 seconden dimmen
  useEffect(() => {
    const initialTimer = setTimeout(() => {
      fadeToTransparent();
    }, 60000);

    return () => {
      clearTimeout(initialTimer);
      if (fadeTimerRef.current) {
        clearTimeout(fadeTimerRef.current);
      }
    };
  }, [fadeToTransparent]);

  // ==========================================
  // Room name genereren — MUST match meetService.ts!
  // Both use: sorted participant IDs, first 8 chars, joined with '-'
  // This ensures ConnectieFlow (VideoPhase) and ChatInterface (JitsiCall)
  // always create the SAME Jitsi room for the same pair of users.
  // ==========================================
  const shortId = roomId
    ? roomId.slice(0, 12)
    : participants && participants.length > 0
      ? participants.map(p => p.uid.slice(0, 8)).sort().join('-')
      : [currentUserId.slice(0, 8), (otherUserId || '').slice(0, 8)].sort().join('-');

  const roomName = `superkonnected-${shortId}`;

  // Determine display name for remote user(s)
  const remoteDisplayName = participants && participants.length > 0
    ? participants.filter(p => p.uid !== currentUserId).map(p => p.name).join(', ')
    : otherUserName || 'Deelnemer';

  // ==========================================
  // ✅ Build Jitsi URL met config via hash parameters
  // Note: String values MUST be JSON-stringified before URL-encoding,
  // otherwise Jitsi's internal JSON.parse() will fail.
  // ==========================================
  const meetingSubject = 'SuperKonnected';
  const jitsiConfig = [
    'config.prejoinConfig.enabled=false',
    'config.prejoinPageEnabled=false',
    'config.requireDisplayName=false',
    'config.startWithAudioMuted=false',
    `config.startWithVideoMuted=${callType === 'audio' ? 'true' : 'false'}`,
    `config.subject=${encodeURIComponent(JSON.stringify(meetingSubject))}`,
    'config.disableDeepLinking=true',
    'config.enableWelcomePage=false',
    'config.enableClosePage=false',
    'config.enableInsecureRoomNameWarning=false',
    'config.enableLobbyChat=false',
    'config.hideLobbyButton=true',
    'config.p2p.enabled=true',
    'interfaceConfig.SHOW_JITSI_WATERMARK=false',
    'interfaceConfig.SHOW_WATERMARK_FOR_GUESTS=false',
    'interfaceConfig.SHOW_BRAND_WATERMARK=false',
    'interfaceConfig.SHOW_POWERED_BY=false',
    'interfaceConfig.SHOW_PROMOTIONAL_CLOSE_PAGE=false',
    'interfaceConfig.SHOW_CHROME_EXTENSION_BANNER=false',
    'interfaceConfig.HIDE_DEEP_LINKING_LOGO=true',
    'interfaceConfig.HIDE_INVITE_MORE_HEADER=true',
    'interfaceConfig.MOBILE_APP_PROMO=false',
    'interfaceConfig.DISPLAY_WELCOME_PAGE_CONTENT=false',
    'interfaceConfig.DISPLAY_WELCOME_FOOTER=false',
    `interfaceConfig.APP_NAME=${encodeURIComponent(JSON.stringify('SuperKonnected'))}`,
    `interfaceConfig.NATIVE_APP_NAME=${encodeURIComponent(JSON.stringify('SuperKonnected'))}`,
    `interfaceConfig.PROVIDER_NAME=${encodeURIComponent(JSON.stringify('SuperKonnected'))}`,
    `interfaceConfig.DEFAULT_REMOTE_DISPLAY_NAME=${encodeURIComponent(JSON.stringify(remoteDisplayName))}`,
    `interfaceConfig.DEFAULT_LOCAL_DISPLAY_NAME=${encodeURIComponent(JSON.stringify(currentUserName || 'Jij'))}`,
    `interfaceConfig.TOOLBAR_BUTTONS=${encodeURIComponent('["microphone","camera","desktop","fullscreen","fodeviceselection","hangup","raisehand","videoquality","filmstrip","tileview","select-background"]')}`,
    `interfaceConfig.DEFAULT_BACKGROUND=${encodeURIComponent(JSON.stringify('#0A2342'))}`,
    `interfaceConfig.AUDIO_LEVEL_PRIMARY_COLOR=${encodeURIComponent(JSON.stringify('rgba(255,107,53,0.6)'))}`,
    `interfaceConfig.AUDIO_LEVEL_SECONDARY_COLOR=${encodeURIComponent(JSON.stringify('rgba(255,107,53,0.3)'))}`,
  ].join('&');

  const userInfoParam = currentUserName
    ? `&userInfo.displayName=${encodeURIComponent(JSON.stringify(currentUserName))}`
    : '';

  const meetUrl = `https://video.superkonnected.com/${roomName}#${jitsiConfig}${userInfoParam}`;

  // ==========================================
  // End call handler
  // ==========================================
  const handleEndCall = useCallback(() => {
    setIsEnding(true);
    setTimeout(() => {
      onEndCall();
    }, 300);
  }, [onEndCall]);

  return (
    <>
      {/* ✅ Donkere backdrop */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 99998,
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
        }}
        onClick={handleEndCall}
      />

      {/* ✅ CALL CONTAINER - past binnen het zichtbare schermgedeelte */}
      <div
        style={{
          position: 'fixed',
          top: '120px',
          left: '8px',
          right: '8px',
          bottom: '60px',
          zIndex: 99999,
          overflow: 'hidden',
          backgroundColor: '#000',
          borderRadius: '12px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(255, 255, 255, 0.08)',
        }}
      >
        <iframe
          ref={iframeRef}
          src={meetUrl}
          allow="camera; microphone; fullscreen; display-capture; autoplay"
          style={{
            width: '100%',
            height: '100%',
            border: 'none',
            display: 'block',
            borderRadius: '12px',
          }}
          title="SuperKonnected Call"
        />

        {/* ✅ Upgrade naar video knop (bij audio call) */}
        {callType === 'audio' && (
          <button
            onClick={() => {
              if (iframeRef.current) {
                const newUrl = iframeRef.current.src.replace(
                  'config.startWithVideoMuted=true',
                  'config.startWithVideoMuted=false'
                );
                iframeRef.current.src = newUrl;
              }
            }}
            style={{
              position: 'absolute',
              bottom: '80px',
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 10,
              padding: '10px 20px',
              backgroundColor: '#2563eb',
              color: '#fff',
              borderRadius: '12px',
              border: 'none',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 500,
              boxShadow: '0 4px 12px rgba(37, 99, 235, 0.4)',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            📹 Upgrade naar video
          </button>
        )}
      </div>

      {/* 
        ✅ OPHANGEN LABEL - Verticale tab links tegen de rand
        Net als Feedback en "Zo werkt SuperKonnected" rechts,
        maar dan rood en links. Vervaagt langzaam naar 10% opacity,
        wordt weer vol zichtbaar bij hover.
      */}
      <div
        style={{
          position: 'fixed',
          left: 0,
          top: '50%',
          transform: 'translateY(-50%)',
          zIndex: 100000,
          isolation: 'isolate',
        }}
      >
        <button
          ref={hangupBtnRef}
          onClick={handleEndCall}
          disabled={isEnding}
          style={{
            writingMode: 'vertical-rl',
            textOrientation: 'mixed',
            padding: '1rem 0.5rem',
            background: isEnding
              ? 'linear-gradient(135deg, #991b1b 0%, #7f1d1d 100%)'
              : 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
            border: 'none',
            borderRadius: '0 0.75rem 0.75rem 0',
            boxShadow: '0 4px 20px rgba(220, 38, 38, 0.4)',
            color: 'white',
            fontSize: '0.875rem',
            fontWeight: 600,
            cursor: isEnding ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            opacity: 1,
            transition: 'opacity 2s ease',
            letterSpacing: '0.5px',
          }}
          onMouseEnter={() => {
            showButton();
          }}
          onMouseLeave={() => {
            scheduleHoverFade();
          }}
          title="Gesprek beëindigen"
          aria-label="Ophangen"
        >
          <span style={{ transform: 'rotate(180deg)' }}>📴</span>
          <span style={{ transform: 'rotate(180deg)', letterSpacing: '1px' }}>
            {isEnding ? 'Beëindigen...' : 'Ophangen'}
          </span>
        </button>
      </div>
    </>
  );
};

export default JitsiCall;
